import Image from "next/image";
import { GiCheckMark } from "react-icons/gi";
import { PersistGate } from "redux-persist/integration/react";

import { translationsContent } from "../../../localization/translations";
import { useAppDispatch } from "../../../redux/hooks";
import {
  addDessertToCart,
  deleteDessertFromCart,
  DessertItem,
} from "../../../redux/slices/cartSlice";
import { persistor } from "../../../redux/store";
import { useLanguage } from "../../../store/lang.context";
import imageLoader from "../../../utils/loader";
import { returnIndex } from "../../../utils/returnIndex";
import { translate } from "../../../utils/translate";
import AddButton from "../../ui/button/AddButton";
import SubtractButton from "../../ui/button/SubtractButton";
import classes from "./dessertItem.module.scss";

interface Props {
  item: DessertItem;
  id: number;
}

const DessertItemCard = ({ item, id }: Props) => {
  const dispatch = useAppDispatch();

  const { language } = useLanguage();

  const handleOnClick = (add: boolean) => {
    if (add) {
      dispatch(addDessertToCart({ id }));
    } else {
      dispatch(deleteDessertFromCart(id));
    }
  };
  const handleDeleteFromCart = () => {
    dispatch(deleteDessertFromCart(id));
  };

  let totalCalories = 100;
  totalCalories += item.coulis.reduce(
    (acc, item) => acc + item.ingredient.calories,
    0
  );
  totalCalories += item.extraCoulis.reduce(
    (acc, item) => acc + item.ingredient.calories,
    0
  );
  totalCalories += item.toppings.reduce(
    (acc, item) => acc + item.ingredient.calories,
    0
  );
  totalCalories += item.extraToppings.reduce(
    (acc, item) => acc + item.ingredient.calories,
    0
  );

  return (
    <PersistGate persistor={persistor} loading={<>Loading...</>}>
      <div className={classes.container}>
        <div className={classes.image}>
          <Image
            src={"dessert_coulis&topping__200x200px.png"}
            height="100%"
            width="100%"
            alt="Dessert sur mesure"
            loader={imageLoader}
          />
        </div>
        <div className={classes.modify}>
          <span onClick={handleDeleteFromCart}>
            {translate(translationsContent, "supprimerButton", language)}
          </span>
        </div>
        <div className={classes.details}>
          <h3>{item.dessert[`title_${language}`] ?? ""}</h3>
          <div className={classes.formulas}>
            {item.toppings.length > 0 || item.coulis.length > 0 ? (
              <p>
                <div className={classes.checkmark}>
                  <GiCheckMark />
                </div>
                <p>
                  {translate(translationsContent, "dessertContent", language)}{" "}
                  {`${
                    item.toppings.length > 0
                      ? item.toppings.map(
                          (topping, i) =>
                            `${i > 0 ? ", " : ""}${
                              topping.ingredient[returnIndex(language)]
                            }`
                        )
                      : ""
                  }
                  `}
                  {item.extraToppings.map(
                    (topping) =>
                      `, ${topping.ingredient[returnIndex(language)]}`
                  )}
                </p>
              </p>
            ) : (
              <></>
            )}
            {item.coulis.length > 0 && (
              <p className={classes.item}>
                <div className={classes.checkmark}>
                  <GiCheckMark />
                </div>
                <p>
                  {item.coulis.map(
                    (coulis, i) =>
                      `${i > 0 ? ", " : " "}${
                        coulis.ingredient[returnIndex(language)]
                      }`
                  )}
                  {item.extraCoulis.map(
                    (coulis) => `, ${coulis.ingredient[returnIndex(language)]}`
                  )}
                </p>
              </p>
            )}
          </div>
          <div className={classes.bottomContainer}>
            <p>
              <span>{totalCalories}</span>{" "}
              {translate(translationsContent, "calories", language)}
            </p>
            <div className={classes.add}>
              <SubtractButton onClick={() => handleOnClick(false)} />
              <div className={classes.quantity}>{item.quantity}</div>
              <AddButton onClick={() => handleOnClick(true)} />
            </div>
            <div className={classes.price}>
              <p className={classes.header}>
                {translate(translationsContent, "ttc", language)}
              </p>
              <p>{(item.formulaTotal * item.quantity).toFixed(2)} €</p>
            </div>
          </div>
        </div>
      </div>
    </PersistGate>
  );
};

export default DessertItemCard;
